/**
 * ChaseTheAce DeckRunner - Theme-var driven sections for GridEngine
 * Fanned deck arc visualization, flippable drawn cards, color-mix() CSS
 */
import React, { useState, useMemo } from 'react'
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Spade from 'lucide-react/dist/esm/icons/spade.js';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import HeroSiteLogo from '../../../components/HeroSiteLogo'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import DrawCountdownHeader from '../../../components/DrawCountdownHeader'
import { useContent } from '../../../contexts/ContentContext'
import { useTranslations } from '../../../contexts/TranslationContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'
import { useCountdown } from '../../../hooks/useCountdown'
import { useLanguage } from '../../../contexts/LanguageContext'
import { useCharityTheme } from '../../../contexts/CharityThemeContext'
import { useSiteConfig } from '../../../contexts/SiteConfigContext'
import { charityConfig } from '../../../config/charity'
import { resolveCtaTargetCard, formatTargetCardName, targetCardString, formatCtaTargetCardText } from '../../../utils/ctaHelpers'

const RED_SUITS = new Set(['♥', '♦'])

// Parse 'A♠', '10♥', 'K♣' into { value, suit }
function parseCard(str) {
  if (!str || typeof str !== 'string') return { value: '?', suit: '?' }
  const suit = str.slice(-1)
  const value = str.slice(0, -1) || '?'
  return { value, suit }
}

/**
 * CardBack - theme-var driven card back
 */
export const CardBack = ({ rot = 0, x = 0, y = 0, z = 0 }) => (
  <div
    className="absolute top-0 left-1/2 origin-bottom"
    style={{
      transform: `translate(calc(-50% + ${x}px), ${y}px) rotate(${rot}deg)`,
      zIndex: z,
    }}
  >
    <div
      className="w-16 h-24 sm:w-20 sm:h-28 lg:w-24 lg:h-36 rounded-lg shadow-xl border"
      style={{
        backgroundColor: 'color-mix(in oklab, var(--charity-primary) 35%, #111)',
        borderColor: 'color-mix(in oklab, var(--charity-primary-contrast) 40%, transparent)',
        backgroundImage:
          'repeating-linear-gradient(45deg, color-mix(in oklab, var(--charity-primary) 12%, transparent) 0 6px, transparent 6px 12px), repeating-linear-gradient(-45deg, color-mix(in oklab, var(--charity-primary-contrast) 10%, transparent) 0 6px, transparent 6px 12px)',
      }}
    >
      <div
        className="w-full h-full flex items-center justify-center rounded-lg"
        style={{ backgroundColor: 'color-mix(in oklab, var(--charity-primary) 30%, transparent)' }}
      >
        <Spade className="w-6 h-6 lg:w-8 lg:h-8 text-white/70" />
      </div>
    </div>
  </div>
)

/**
 * DrawnCard - Face-up drawn card (flippable with week number on back)
 */
export const DrawnCard = ({ card, index, flippedIdx, setFlippedIdx, targetCard }) => {
  const { t: tr } = useTranslations()
  const { value, suit } = parseCard(card)
  const isRed = RED_SUITS.has(suit)
  const isFlipped = flippedIdx === index
  const isAce = value === targetCard?.rank && suit === targetCard?.suit
  return (
    <button
      type="button"
      onClick={() => setFlippedIdx(isFlipped ? null : index)}
      onMouseEnter={() => setFlippedIdx(index)}
      onMouseLeave={() => setFlippedIdx(prev => (prev === index ? null : prev))}
      className={`relative w-10 h-14 sm:w-12 sm:h-16 rounded-md shadow-lg bg-white border-2 transition-transform hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-white/70 ${
        isAce ? 'ring-2 ring-amber-400' : ''
      }`}
      style={{
        borderColor: isAce
          ? '#f59e0b'
          : 'color-mix(in oklab, var(--charity-primary) 25%, #d1d5db)',
      }}
      aria-label={tr('deckRunner.drawnCard', 'Drawn card {card}').replace('{card}', `${value}${suit}`)}
    >
      {isFlipped ? (
        <div
          className="absolute inset-0 rounded-md flex flex-col items-center justify-center text-white text-[10px] font-bold p-1"
          style={{ backgroundColor: 'var(--charity-primary)' }}
        >
          <span className="uppercase tracking-wider opacity-80">{tr('chaseTheAce.week', 'Week')}</span>
          <span className="text-lg font-black leading-none">{index + 1}</span>
        </div>
      ) : (
        <div
          className={`absolute inset-0 flex flex-col items-center justify-center ${
            isRed ? 'text-red-600' : 'text-gray-900'
          }`}
        >
          <span className="text-sm sm:text-base font-black leading-none">{value}</span>
          <span className="text-base sm:text-lg leading-none">{suit}</span>
        </div>
      )}
    </button>
  )
}

/**
 * Hero section - Fanned deck arc + drawn cards row + jackpot + countdown
 */
export function ChaseDeckRunnerHeroSection({
  mainDraw,
  partner,
  eliminatedCards = [],
  onLoginClick,
  editMode,
  handleTranslationUpdate,
  sections = [],
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const { branding } = useCharityTheme()
  const { siteDetails } = useSiteConfig() || {}
  const { timeLeft } = useCountdown(mainDraw, { enableAnimation: true })
  const [flippedCardIdx, setFlippedCardIdx] = useState(null)

  const { language } = useLanguage()

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const weeklyPrize = Math.floor(mainDraw?.weekly_prize ?? mainDraw?.secondary_prize ?? 0)
  const cardsRemaining = mainDraw?.cards_remaining ?? (52 - eliminatedCards.length)
  const ctaTarget = resolveCtaTargetCard(sections)
  const ctaTargetStr = targetCardString(ctaTarget)
  const ctaTargetName = formatTargetCardName(ctaTarget.rank, ctaTarget.suit, language)
  const aceFound = eliminatedCards.includes(ctaTargetStr)
  const targetCopy = (key, fallback) => formatCtaTargetCardText(c(key, fallback), ctaTarget, fallback, language)

  const heroGradientStyle = {
    backgroundImage:
      'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
    color: 'var(--charity-primary-contrast)',
  }

  // Fanned deck
  const fanCards = useMemo(() => {
    const visible = Math.max(1, Math.min(14, cardsRemaining))
    const mid = (visible - 1) / 2
    return Array.from({ length: visible }, (_, i) => {
      const offset = i - mid
      const rot = offset * 6
      const x = offset * 22
      const y = Math.abs(offset) * 6
      return { rot, x, y, i }
    })
  }, [cardsRemaining])

  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl
  const brandName = siteDetails?.siteName || charityConfig.name
  const defaultPlatformLogo = 'https://cms.tlffs.ca/uploads/header_Logo_4f095fb08b.svg'
  const isDefaultLogo = branding?.logoUrl === defaultPlatformLogo
  const logoUrl = isDefaultLogo ? brandLogo : branding?.logoUrl || brandLogo
  const logoAlt = branding?.charityName || brandName || tr('common.charityPartner', 'Charity Partner')

  return (
    <section
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      style={heroGradientStyle}
    >
      <div className="absolute inset-0 bg-black/15 pointer-events-none" aria-hidden="true" />

      {/* Floating suit glyph decorations */}
      <div
        aria-hidden="true"
        className="absolute top-10 left-[8%] text-7xl font-black opacity-10 select-none"
        style={{ color: 'var(--charity-primary-contrast)' }}
      >
        ♠
      </div>
      <div
        aria-hidden="true"
        className="absolute top-24 right-[12%] text-6xl font-black opacity-10 select-none"
        style={{ color: 'var(--charity-primary-contrast)' }}
      >
        ♥
      </div>
      <div
        aria-hidden="true"
        className="absolute bottom-24 left-[14%] text-6xl font-black opacity-10 select-none"
        style={{ color: 'var(--charity-primary-contrast)' }}
      >
        ♦
      </div>
      <div
        aria-hidden="true"
        className="absolute bottom-16 right-[10%] text-7xl font-black opacity-10 select-none"
        style={{ color: 'var(--charity-primary-contrast)' }}
      >
        ♣
      </div>

      {/* Glow orbs */}
      <div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ backgroundColor: 'var(--charity-primary-contrast)' }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl opacity-10 pointer-events-none"
        style={{ backgroundColor: 'var(--charity-primary-contrast)' }}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <HeroSiteLogo tone="dark" />
        <HeroPartnerGlassSelect className="mx-auto mb-6 w-full max-w-md" />
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          {/* LEFT — deck spread + drawn row */}
          <div className="lg:col-span-7 text-center flex flex-col items-center">
            {logoUrl && (
              <div className="mb-6 flex justify-center">
                <div className="relative group">
                  <div
                    className="absolute -inset-3 rounded-2xl blur-xl opacity-40"
                    style={{ backgroundColor: 'var(--charity-primary-contrast)' }}
                    aria-hidden="true"
                  />
                  <div className="relative bg-white rounded-2xl px-6 py-4 shadow-2xl">
                    <img
                      src={logoUrl}
                      alt={logoAlt}
                      className="h-14 sm:h-16 lg:h-20 w-auto object-contain max-w-[260px]"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white rounded-full text-sm font-bold mb-5">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
              </span>
              {mainDraw?.name || (
                <InlineTranslationEdit
                  translationKey="deckRunner.liveLabel"
                  value={c('deckRunner.liveLabel')}
                  fallback="Chase the Ace — LIVE"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              )}
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-3 leading-tight">
              <InlineTranslationEdit
                translationKey="deckRunner.heroTitle"
                value={c('deckRunner.heroTitle')}
                fallback="Chase the Ace"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-6 max-w-xl mx-auto">
              <span className="font-bold">{cardsRemaining}</span>{' '}
              <InlineTranslationEdit
                translationKey="deckRunner.cardsRemaining"
                value={c('deckRunner.cardsRemaining')}
                fallback="cards remaining"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />{' '}
              · {aceFound
                ? tr('deckRunner.targetFound', '{targetCard} found!').replace('{targetCard}', ctaTargetName)
                : tr('deckRunner.targetOdds', '1 in {count} to find the {targetCard}')
                  .replace('{count}', cardsRemaining)
                  .replace('{targetCard}', ctaTargetName)}
            </p>

            {/* Fanned deck */}
            <div
              className="relative w-full h-48 sm:h-56 lg:h-64 mx-auto mb-6"
              aria-label={tr('chaseTheAce.cardsRemaining', '{{count}} cards remaining').replace('{{count}}', cardsRemaining)}
            >
              {fanCards.map((f, idx) => (
                <CardBack key={idx} rot={f.rot} x={f.x} y={f.y} z={idx} />
              ))}
              <div
                className="absolute bottom-0 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider shadow-lg"
                style={{
                  backgroundColor: 'color-mix(in oklab, var(--charity-primary-contrast) 90%, transparent)',
                  color: 'var(--charity-primary)',
                }}
              >
                {cardsRemaining} {tr('chaseTheAce.inDeckLabel', 'In Deck')}
              </div>
            </div>

            {/* Drawn cards row */}
            {eliminatedCards.length > 0 && (
              <div className="w-full">
                <p className="text-xs uppercase tracking-wider text-white/70 font-semibold mb-2">
                  <InlineTranslationEdit
                    translationKey="deckRunner.drawnSoFar"
                    value={c('deckRunner.drawnSoFar')}
                    fallback="Drawn so far"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                  {' '}· {eliminatedCards.length}
                </p>
                <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 max-w-full">
                  {eliminatedCards.map((card, idx) => (
                    <DrawnCard
                      key={`${card}-${idx}`}
                      card={card}
                      index={idx}
                      flippedIdx={flippedCardIdx}
                      setFlippedIdx={setFlippedCardIdx}
                      targetCard={ctaTarget}
                    />
                  ))}
                </div>
                <p className="text-[10px] text-white/60 mt-2">
                  <InlineTranslationEdit
                    translationKey="deckRunner.flipHint"
                    value={c('deckRunner.flipHint')}
                    fallback="Hover or tap a card to see the week it was drawn"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </p>
              </div>
            )}
          </div>

          {/* RIGHT — jackpot + countdown + CTA */}
          <div className="lg:col-span-5 flex flex-col items-center lg:items-end gap-5">
            <div className="relative w-full max-w-md">
              <div className="absolute -inset-4 rounded-3xl blur-xl opacity-40 bg-white" aria-hidden="true" />
              <div className="relative bg-white/10 backdrop-blur-md rounded-3xl p-6 shadow-2xl border border-white/30">
                <p className="text-white/80 font-bold uppercase text-xs tracking-wider mb-2 text-center">
                  <InlineTranslationEdit
                    translationKey="deckRunner.jackpotLabel"
                    value={targetCopy('deckRunner.jackpotLabel', `${ctaTargetName} Jackpot`)}
                    fallback={`${ctaTargetName} Jackpot`}
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </p>
                <p className="text-5xl md:text-6xl xl:text-7xl font-black text-center mb-4 text-white">
                  ${jackpotAmount.toLocaleString()}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    className="rounded-xl p-3 text-center border border-white/30"
                    style={{
                      backgroundColor: 'color-mix(in oklab, var(--charity-primary) 22%, transparent)',
                    }}
                  >
                    <Trophy className="w-5 h-5 mx-auto mb-1 text-white" />
                    <p className="text-[10px] mb-0.5 text-white/80 uppercase">{tr('chaseTheAce.weeklyPrize', 'Weekly Prize')}</p>
                    <p className="text-xl font-black text-white">${weeklyPrize.toLocaleString()}</p>
                  </div>
                  <div
                    className="rounded-xl p-3 text-center border border-white/30"
                    style={{
                      backgroundColor:
                        'color-mix(in oklab, var(--charity-secondary, var(--charity-primary)) 22%, transparent)',
                    }}
                  >
                    <Spade className="w-5 h-5 mx-auto mb-1 text-white" />
                    <p className="text-[10px] mb-0.5 text-white/80 uppercase">{tr('chaseTheAce.cardsLeft', 'Cards Left')}</p>
                    <p className="text-xl font-black text-white">{cardsRemaining}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Countdown */}
            <div className="bg-white/15 backdrop-blur-sm rounded-2xl px-5 py-4 border border-white/30 w-full max-w-md">
              <DrawCountdownHeader draw={mainDraw} className="mb-3" labelClassName="text-white/80">
                <InlineTranslationEdit
                  translationKey="deckRunner.drawIn"
                  value={c('deckRunner.drawIn')}
                  fallback="Next Draw In"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </DrawCountdownHeader>
              <div className="flex gap-3 justify-center">
                {[
                  { val: timeLeft.days, label: tr('countdown.days', 'Days') },
                  { val: timeLeft.hours, label: tr('countdown.hours', 'Hrs') },
                  { val: timeLeft.minutes, label: tr('countdown.minutes', 'Min') },
                  { val: timeLeft.seconds, label: tr('countdown.seconds', 'Sec') },
                ].map((item, i) => (
                  <div key={i} className="flex flex-col items-center">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/25 backdrop-blur-sm rounded-xl flex items-center justify-center text-xl sm:text-2xl font-black text-white shadow-lg border border-white/30">
                      {String(item.val).padStart(2, '0')}
                    </div>
                    <span className="text-[10px] text-white/70 mt-1 font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={() => purchaseNavigate('/chase-the-ace-purchase')}
              className="group relative inline-flex items-center justify-center gap-3 w-full sm:w-auto px-8 py-4 bg-white text-lg font-black rounded-2xl transition-all hover:scale-105 hover:shadow-2xl shadow-xl"
              style={{ color: 'var(--charity-primary)' }}
            >
              <Ticket className="w-5 h-5" />
              <InlineTranslationEdit
                translationKey="deckRunner.getTickets"
                value={c('deckRunner.getTickets')}
                fallback="Grab Your Ticket"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * Jackpot Summary section - Overlapping strip below hero (-mt-12)
 */
export function ChaseDeckRunnerJackpotSummarySection({
  mainDraw,
  onLoginClick,
  editMode,
  handleTranslationUpdate,
  sections = [],
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const cardsRemaining = mainDraw?.cards_remaining ?? 52
  const { language } = useLanguage()
  const ctaTarget = resolveCtaTargetCard(sections)
  const ctaTargetName = formatTargetCardName(ctaTarget.rank, ctaTarget.suit, language)
  const targetCopy = (key, fallback) => formatCtaTargetCardText(c(key, tr(key, fallback)), ctaTarget, fallback, language)

  return (
    <section className="relative z-20 -mt-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid md:grid-cols-3 gap-6 items-center">
            <div className="text-center md:text-left">
              <p
                className="text-sm font-semibold uppercase tracking-wider"
                style={{ color: 'var(--charity-primary)' }}
              >
                {targetCopy('deckRunner.jackpotLabel', `${ctaTargetName} Jackpot`)}
              </p>
              <p className="text-3xl md:text-4xl font-black text-gray-900 dark:text-white">
                ${jackpotAmount.toLocaleString()}
              </p>
            </div>
            <div className="text-center border-l border-r border-gray-200 dark:border-gray-700 px-6">
              <p
                className="text-sm font-semibold uppercase tracking-wider"
                style={{ color: 'var(--charity-secondary, var(--charity-primary))' }}
              >
                {tr('deckRunner.odds', 'Odds · 1 in {count}').replace('{count}', cardsRemaining)}
              </p>
              <p className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">
                {tr('chaseTheAce.cardsRemaining', '{{count}} cards remaining').replace('{{count}}', cardsRemaining)}
              </p>
            </div>
            <div className="text-center md:text-right">
              <button
                onClick={() => purchaseNavigate('/chase-the-ace-purchase')}
                className="inline-flex items-center gap-2 px-6 py-3 text-white font-bold rounded-xl hover:scale-105 transition-all shadow-lg"
                style={{
                  backgroundImage:
                    'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
                  color: 'var(--charity-primary-contrast)',
                }}
              >
                <Ticket className="w-5 h-5" />
                {tr('chaseTheAce.getTickets', 'Get Tickets')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * Stats & Winners section - 2-3 column: stats impact + optional winners
 */
export function ChaseDeckRunnerStatsWinnersSection({ mainDraw, editMode, handleTranslationUpdate, sections = [] }) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const weeklyPrize = Math.floor(mainDraw?.weekly_prize ?? mainDraw?.secondary_prize ?? 0)
  const cardsRemaining = mainDraw?.cards_remaining ?? 52
  const ticketsSold = mainDraw?.total_tickets_sold
  const { language } = useLanguage()
  const ctaTarget = resolveCtaTargetCard(sections)
  const ctaTargetName = formatTargetCardName(ctaTarget.rank, ctaTarget.suit, language)

  return (
    <section className="py-12 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <TrendingUp className="w-6 h-6" style={{ color: 'var(--charity-primary)' }} />
            {tr('deckRunner.drawImpact', "This Draw's Impact")}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-gray-900">
              <DollarSign className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--charity-primary)' }} />
              <p className="text-2xl font-black text-gray-900 dark:text-white">
                ${jackpotAmount.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{tr('deckRunner.jackpotLabel', '{{targetCard}} Jackpot').replace('{{targetCard}}', ctaTargetName)}</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-gray-900">
              <Trophy
                className="w-8 h-8 mx-auto mb-2"
                style={{ color: 'var(--charity-secondary, var(--charity-primary))' }}
              />
              <p className="text-2xl font-black text-gray-900 dark:text-white">
                ${weeklyPrize.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{tr('chaseTheAce.weeklyPrize', 'Weekly Prize')}</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-gray-900">
              <Spade
                className="w-8 h-8 mx-auto mb-2"
                style={{ color: 'var(--charity-accent, var(--charity-primary))' }}
              />
              <p className="text-2xl font-black text-gray-900 dark:text-white">{cardsRemaining}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{tr('chaseTheAce.cardsLeft', 'Cards Left')}</p>
            </div>
            {ticketsSold != null && (
              <div className="text-center p-4 rounded-xl bg-gray-100 dark:bg-gray-900">
                <Users className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                <p className="text-2xl font-black text-gray-900 dark:text-white">
                  {ticketsSold.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{tr('chaseTheAce.ticketsSold', 'Tickets Sold')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * How It Works section - 4 steps with color-mix() CSS tinting
 */
export function ChaseDeckRunnerHowItWorksSection({ mainDraw, editMode, handleTranslationUpdate, sections = [] }) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const { language } = useLanguage()
  const ticketPrice = mainDraw?.ticket_price ?? 5
  const charityName = mainDraw?.charity_name ?? tr('classic5050.localCharity', 'Local Charity')
  const ctaTarget = resolveCtaTargetCard(sections)
  const ctaTargetName = formatTargetCardName(ctaTarget.rank, ctaTarget.suit, language)
  const translatedTargetCopy = (key, fallback) => formatCtaTargetCardText(tr(key, fallback), ctaTarget, fallback, language)

  const steps = [
    {
      icon: '🎟️',
      step: '1',
      title: tr('chaseTheAce.step1Title', 'Buy Tickets'),
      desc: tr('deckRunner.ticketSupports', '{price} per ticket · supports {charity}')
        .replace('{price}', `$${ticketPrice}`)
        .replace('{charity}', charityName),
    },
    {
      icon: '✉️',
      step: '2',
      title: tr('chaseTheAce.step2Title', 'Weekly Draw'),
      desc: tr('chaseTheAce.step2Desc', 'A random ticket is drawn each week.'),
    },
    {
      icon: '✉️',
      step: '3',
      title: translatedTargetCopy('chaseTheAce.step3Title', 'Reveal the Envelope'),
      desc: translatedTargetCopy('chaseTheAce.step3Desc', 'The chosen envelope is opened live to reveal the card inside.'),
    },
    {
      icon: '🏆',
      step: '4',
      title: tr('chaseTheAce.rule2Title', 'Jackpot Grows'),
      desc: translatedTargetCopy('chaseTheAce.rule2Desc', 'Miss the Ace? The jackpot rolls over and odds get better every week.'),
    },
  ]

  const tintBg = 'color-mix(in oklab, var(--charity-primary) 8%, transparent)'
  const tintBorder = 'color-mix(in oklab, var(--charity-primary) 25%, transparent)'

  return (
    <section className="py-12 bg-white dark:bg-gray-800 border-y border-gray-200 dark:border-gray-700">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
            {translatedTargetCopy('chaseTheAce.howItWorksTitle', 'How Chase the Ace Works')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {translatedTargetCopy('chaseTheAce.howItWorksDesc', 'A classic card-draw fundraiser — the fewer cards left, the bigger your shot at the Ace.')}
          </p>
        </div>
        <div className="grid md:grid-cols-4 gap-6">
          {steps.map((item, i) => (
            <div key={i} className="relative text-center">
              {i < 3 && (
                <div
                  className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5"
                  style={{
                    backgroundImage:
                      'linear-gradient(to right, color-mix(in oklab, var(--charity-primary) 40%, transparent), transparent)',
                  }}
                />
              )}
              <div className="relative">
                <div
                  className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center text-3xl shadow-md"
                  style={{ backgroundColor: tintBg, border: `1px solid ${tintBorder}` }}
                >
                  {item.icon}
                </div>
                <div
                  className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shadow"
                  style={{
                    backgroundColor: 'var(--charity-primary)',
                    color: 'var(--charity-primary-contrast)',
                  }}
                >
                  {item.step}
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/**
 * Final CTA section - "Will you pull the Ace?" themed
 */
export function ChaseDeckRunnerFinalCTASection({
  mainDraw,
  eliminatedCards = [],
  onLoginClick,
  editMode,
  handleTranslationUpdate,
  sections = [],
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const { siteDetails } = useSiteConfig() || {}
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const cardsRemaining = mainDraw?.cards_remaining ?? (52 - eliminatedCards.length)
  const { language } = useLanguage()
  const ctaTarget = resolveCtaTargetCard(sections, siteDetails)
  const ctaTargetName = formatTargetCardName(ctaTarget.rank, ctaTarget.suit, language)
  const targetCopy = (key, fallback) => formatCtaTargetCardText(c(key, fallback), ctaTarget, fallback, language)

  return (
    <section
      className="py-16 relative overflow-hidden"
      style={{
        backgroundImage:
          'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
        color: 'var(--charity-primary-contrast)',
      }}
    >
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 text-9xl">♠</div>
        <div className="absolute bottom-10 right-10 text-9xl">♠</div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
          <InlineTranslationEdit
            translationKey="deckRunner.finalCTATitle"
            value={targetCopy('deckRunner.finalCTATitle', `Will You Pull the ${ctaTargetName}?`)}
            fallback={`Will You Pull the ${ctaTargetName}?`}
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h2>

        <p className="text-xl text-white/90 mb-8">
          <span className="font-bold">{cardsRemaining}</span>{' '}
          <InlineTranslationEdit
            translationKey="chaseTheAce.cardsLeft"
            value={c('chaseTheAce.cardsLeft')}
            fallback="cards remaining"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />{' '}
          • <span className="font-bold">{(tr('chaseTheAce.cardsOdds') || '1 in {{count}} odds').replace('{{count}}', cardsRemaining)}</span>{' '}
          <span className="font-bold">${jackpotAmount.toLocaleString()}</span>
        </p>

        <button
          onClick={() => purchaseNavigate('/chase-the-ace-purchase')}
          className="group inline-flex items-center gap-3 px-12 py-5 bg-white text-xl font-black rounded-2xl shadow-2xl hover:scale-105 transition-all"
          style={{ color: 'var(--charity-primary)' }}
        >
          <Ticket className="w-6 h-6" />
          <InlineTranslationEdit
            translationKey="chaseTheAce.getTicketsNow"
            value={c('chaseTheAce.getTicketsNow')}
            fallback="Get Your Tickets Now"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </section>
  )
}
